import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  Palette, 
  Loader2, 
  Download, 
  Sparkles,
  Building2,
  Briefcase,
  Heart,
  Zap,
  Circle,
  ArrowLeft,
  Home
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const INDUSTRIES = [
  "Technology", "Marketing", "Finance", "Healthcare", "Education", 
  "E-commerce", "Real Estate", "Food & Beverage", "Fashion", "Fitness"
];

const STYLES = [
  { id: "modern", name: "Modern", icon: <Zap className="w-4 h-4" /> },
  { id: "classic", name: "Classic", icon: <Building2 className="w-4 h-4" /> },
  { id: "playful", name: "Playful", icon: <Heart className="w-4 h-4" /> },
  { id: "minimal", name: "Minimal", icon: <Circle className="w-4 h-4" /> },
  { id: "bold", name: "Bold", icon: <Briefcase className="w-4 h-4" /> }
];

export default function BrandKitGeneratorPage() {
  const navigate = useNavigate();
  const [brandName, setBrandName] = useState("");
  const [industry, setIndustry] = useState("Technology");
  const [style, setStyle] = useState("modern");
  const [loading, setLoading] = useState(false);
  const [brandKit, setBrandKit] = useState(null);

  const handleGenerate = async () => {
    if (!brandName.trim()) {
      toast.error("Please enter your brand name");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/image-gen/brand-kit`, {
        brand_name: brandName.trim(),
        industry,
        style
      });

      if (response.data.success) {
        setBrandKit(response.data);
        toast.success("Brand kit generated!");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate brand kit");
    } finally {
      setLoading(false);
    }
  };

  const downloadAsset = (dataUrl, type) => {
    const link = document.createElement('a');
    link.href = dataUrl;
    link.download = `${brandName.toLowerCase().replace(/\s+/g, '-')}-${type}.png`;
    link.click();
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-violet-600 to-indigo-600 text-white py-12">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex items-center gap-4 mb-4">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="text-white hover:bg-white/20">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <Button variant="ghost" size="icon" onClick={() => navigate("/")} className="text-white hover:bg-white/20">
              <Home className="w-5 h-5" />
            </Button>
          </div>
          <div className="flex items-center gap-3 mb-2">
            <div className="p-3 bg-white/20 rounded-xl">
              <Palette className="w-8 h-8" />
            </div>
            <div>
              <h1 className="text-3xl font-bold">Brand Kit Generator</h1>
              <p className="text-violet-200">Create a complete brand identity with AI</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Input Section */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Brand Details</CardTitle>
                <CardDescription>Tell us about your brand</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-sm font-medium mb-1 block">Brand Name</label>
                  <Input
                    placeholder="e.g., TechFlow, GreenLeaf, SwiftPay"
                    value={brandName}
                    onChange={(e) => setBrandName(e.target.value)}
                  />
                </div>

                <div>
                  <label className="text-sm font-medium mb-2 block">Industry</label>
                  <div className="flex flex-wrap gap-2">
                    {INDUSTRIES.map((ind) => (
                      <button
                        key={ind}
                        onClick={() => setIndustry(ind)}
                        className={`px-3 py-1.5 rounded-full text-sm transition-all ${
                          industry === ind 
                            ? "bg-violet-600 text-white" 
                            : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                        }`}
                      >
                        {ind}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium mb-2 block">Style</label>
                  <div className="grid grid-cols-5 gap-2">
                    {STYLES.map((s) => (
                      <button
                        key={s.id}
                        onClick={() => setStyle(s.id)}
                        className={`p-3 rounded-lg border text-center transition-all ${
                          style === s.id 
                            ? "border-violet-500 bg-violet-50 text-violet-700" 
                            : "border-gray-200 hover:border-gray-300"
                        }`}
                      >
                        <div className="flex justify-center mb-1">{s.icon}</div>
                        <span className="text-xs">{s.name}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <Button
                  onClick={handleGenerate}
                  disabled={loading || !brandName.trim()}
                  className="w-full bg-gradient-to-r from-violet-600 to-indigo-600"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Generating Kit... (1-2 min)
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" />
                      Generate Brand Kit
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>

            {/* Color Palette Preview */}
            {brandKit && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Color Palette</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex gap-2">
                    {brandKit.colors?.map((color, i) => (
                      <div key={i} className="flex-1 text-center">
                        <div 
                          className="h-16 rounded-lg mb-2 shadow-inner"
                          style={{ backgroundColor: color }}
                        />
                        <span className="text-xs text-gray-600">{color}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Output Section */}
          <div>
            {brandKit ? (
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle>{brandKit.brand_name} Brand Kit</CardTitle>
                    <Badge className="bg-green-100 text-green-700">Ready</Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-6">
                  {brandKit.assets?.map((asset, index) => (
                    <div key={index} className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="font-medium capitalize">{asset.type}</span>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => downloadAsset(asset.data_url, asset.type)}
                        >
                          <Download className="w-3 h-3 mr-1" />
                          Download
                        </Button>
                      </div>
                      <div className="rounded-lg overflow-hidden border bg-gray-50 p-4">
                        <img 
                          src={asset.data_url} 
                          alt={asset.type} 
                          className="w-full h-auto max-h-48 object-contain"
                        />
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            ) : (
              <Card className="h-full min-h-[500px] flex items-center justify-center bg-gray-50/50">
                <div className="text-center p-8">
                  <Palette className="w-20 h-20 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500 text-lg">Your brand kit will appear here</p>
                  <p className="text-sm text-gray-400 mt-2">Includes logo, profile picture, and banner</p>
                </div>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
